import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import { useNavigate } from "react-router-dom";
import logoMatches from "../../static/Home/logo_matches.webp";

const statusConfig = {
  live: {
    label: "EN VIVO",
    bg: "rgba(211, 47, 47, 0.12)",
    color: "#d32f2f",
  },
  scheduled: {
    label: "PROGRAMADO",
    bg: "rgba(6, 78, 42, 0.12)",
    color: "#064e2a",
  },
  finished: {
    label: "FINALIZADO",
    bg: "rgba(21, 128, 61, 0.14)",
    color: "#15803d",
  },
  cancelled: {
    label: "CANCELADO",
    bg: "rgba(97, 97, 97, 0.14)",
    color: "#616161",
  },
};

function TeamLogo({ src, alt }) {
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: { xs: 52, sm: 74, md: 88 },
          height: { xs: 52, sm: 74, md: 88 },
          objectFit: "contain",
          flexShrink: 0,
          filter: "drop-shadow(0 8px 14px rgba(6,78,42,0.14))",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: { xs: 52, sm: 74, md: 88 },
        height: { xs: 52, sm: 74, md: 88 },
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background:
          "linear-gradient(180deg, #ffffff 0%, #e8f5ec 100%)",
        border: "1px solid rgba(6,78,42,0.20)",
        boxShadow: "0 8px 18px rgba(6,78,42,0.14)",
      }}
    >
      <SportsSoccerRoundedIcon
        sx={{
          fontSize: { xs: 30, sm: 42, md: 50 },
          color: "#064e2a",
        }}
      />
    </Box>
  );
}

export default function NextMatchesSection() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const pollTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);

  const formatDateLabel = (date, time) => {
    const today = new Date();
    const matchDate = new Date(`${date}T12:00:00`);

    const isToday =
      today.getFullYear() === matchDate.getFullYear() &&
      today.getMonth() === matchDate.getMonth() &&
      today.getDate() === matchDate.getDate();

    if (isToday) return `HOY · ${time}`;

    return `${matchDate.toLocaleDateString("es-EC", {
      day: "numeric",
      month: "short",
    })} · ${time}`;
  };

  const getStatusMeta = (status) => {
    return statusConfig[status] || statusConfig.scheduled;
  };

  const clearScheduledPoll = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const scheduleNextPoll = useCallback((seconds) => {
    clearScheduledPoll();

    const delay = Number(seconds);

    if (!Number.isFinite(delay) || delay <= 0) {
      return;
    }

    pollTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        loadHomeState();
      }
    }, delay * 1000);
  }, []);

  const loadHomeState = useCallback(async () => {
    try {
      const res = await fetch("/matches/home-next-state/");
      const data = await res.json();

      if (!isMountedRef.current) return;

      const safeMatches = Array.isArray(data?.matches) ? data.matches : [];
      setMatches(safeMatches);
      scheduleNextPoll(data?.next_check_in_seconds);
    } catch {
      if (!isMountedRef.current) return;
      setMatches([]);
      scheduleNextPoll(30);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [scheduleNextPoll]);

  useEffect(() => {
    isMountedRef.current = true;

    loadHomeState();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadHomeState();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearScheduledPoll();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadHomeState]);

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f4fbf6 45%, #e8f5ec 100%)",
        py: { xs: 5, md: 8 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(6,78,42,0.13), transparent 68%)",
          top: -160,
          left: -120,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(21,128,61,0.12), transparent 70%)",
          bottom: -190,
          right: -130,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.08,
          backgroundImage:
            "linear-gradient(#064e2a 1px, transparent 1px), linear-gradient(90deg, #064e2a 1px, transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 1040,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: ".8rem", md: ".9rem" },
              fontWeight: 900,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "#064e2a",
              mb: 1,
            }}
          >
            Liga Deportiva Cantonal Mejía
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "1.8rem", md: "2.45rem" },
              fontWeight: 900,
              color: "#052e16",
              mb: 1,
              lineHeight: 1.12,
            }}
          >
            Partidos destacados
          </Typography>

          <Typography
            sx={{
              color: "#3f5f4b",
              mb: 4,
              fontWeight: 600,
              fontSize: { xs: ".95rem", md: "1.05rem" },
            }}
          >
            Marcadores en vivo, próximos encuentros y programación oficial del torneo de fútbol
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress sx={{ color: "#064e2a" }} />
            </Box>
          ) : matches.length ? (
            <Stack spacing={2.4} alignItems="center">
              {matches.map((match) => {
                const statusMeta = getStatusMeta(match.status);
                const isLive = match.status === "live";
                const isScheduled = match.status === "scheduled";

                return (
                  <Card
                    key={match.id}
                    elevation={0}
                    sx={{
                      width: "100%",
                      maxWidth: 940,
                      borderRadius: "26px",
                      px: { xs: 1.4, sm: 2.2, md: 3 },
                      py: { xs: 1.8, sm: 2.2, md: 2.8 },
                      background:
                        "linear-gradient(180deg, #ffffff 0%, #fbfffc 100%)",
                      border: "1px solid rgba(6,78,42,0.12)",
                      boxShadow: "0 22px 52px rgba(6,78,42,0.13)",
                    }}
                  >
                    <Stack spacing={{ xs: 1.4, md: 1.8 }}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.4,
                            py: 0.6,
                            borderRadius: "999px",
                            backgroundColor: statusMeta.bg,
                            color: statusMeta.color,
                            fontWeight: 900,
                            fontSize: { xs: ".7rem", sm: ".75rem" },
                            lineHeight: 1,
                          }}
                        >
                          {isLive && (
                            <Box
                              sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                backgroundColor: "#d32f2f",
                                boxShadow: "0 0 0 4px rgba(211,47,47,0.16)",
                              }}
                            />
                          )}
                          {statusMeta.label}
                        </Box>
                      </Box>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={{ xs: 1, sm: 1.8, md: 2.4 }}
                        sx={{ width: "100%" }}
                      >
                        <Stack
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                          }}
                        >
                          <TeamLogo
                            src={match.team_local_logo}
                            alt={match.team_local}
                          />

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: {
                                xs: ".86rem",
                                sm: "1rem",
                                md: "1.08rem",
                              },
                              textAlign: "center",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "#052e16",
                            }}
                          >
                            {match.team_local}
                          </Typography>
                        </Stack>

                        <Stack
                          spacing={{ xs: 1, sm: 1.2 }}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flexShrink: 0,
                            minWidth: { xs: 148, sm: 220, md: 260 },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={{ xs: 0.6, sm: 1.1, md: 1.3 }}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Box
                              sx={{
                                minWidth: { xs: 46, sm: 72, md: 84 },
                                height: { xs: 52, sm: 74, md: 84 },
                                px: { xs: 0.8, sm: 1.2 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "18px",
                                background:
                                  "linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)",
                                border: "1px solid rgba(6,78,42,0.18)",
                                boxShadow: "0 12px 26px rgba(6,78,42,0.10)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  fontSize: {
                                    xs: "1.8rem",
                                    sm: "2.7rem",
                                    md: "3.1rem",
                                  },
                                  color: "#064e2a",
                                  lineHeight: 1,
                                }}
                              >
                                {isScheduled ? "-" : match.score_local ?? 0}
                              </Typography>
                            </Box>

                            <Box
                              component="img"
                              src={logoMatches}
                              alt="vs"
                              sx={{
                                width: { xs: 24, sm: 34, md: 38 },
                                height: { xs: 24, sm: 34, md: 38 },
                                objectFit: "contain",
                                flexShrink: 0,
                              }}
                            />

                            <Box
                              sx={{
                                minWidth: { xs: 46, sm: 72, md: 84 },
                                height: { xs: 52, sm: 74, md: 84 },
                                px: { xs: 0.8, sm: 1.2 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "18px",
                                background:
                                  "linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)",
                                border: "1px solid rgba(6,78,42,0.18)",
                                boxShadow: "0 12px 26px rgba(6,78,42,0.10)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  fontSize: {
                                    xs: "1.8rem",
                                    sm: "2.7rem",
                                    md: "3.1rem",
                                  },
                                  color: "#064e2a",
                                  lineHeight: 1,
                                }}
                              >
                                {isScheduled ? "-" : match.score_visitor ?? 0}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: ".76rem", sm: ".86rem" },
                              color: "#3f5f4b",
                              letterSpacing: ".04em",
                            }}
                          >
                            {match.category}
                          </Typography>
                        </Stack>

                        <Stack
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                          }}
                        >
                          <TeamLogo
                            src={match.team_visitor_logo}
                            alt={match.team_visitor}
                          />

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: {
                                xs: ".86rem",
                                sm: "1rem",
                                md: "1.08rem",
                              },
                              textAlign: "center",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "#052e16",
                            }}
                          >
                            {match.team_visitor}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={0.55} alignItems="center" justifyContent="center">
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: { xs: ".82rem", sm: ".9rem" },
                            color: "#3f5f4b",
                            fontWeight: 800,
                            lineHeight: 1.2,
                          }}
                        >
                          {formatDateLabel(match.date, match.time)} · {match.venue}
                        </Typography>
                      </Stack>

                      {isLive && (
                        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.4 }}>
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/partido/${match.id}`)}
                            sx={{
                              minWidth: { xs: 170, sm: 190 },
                              height: 46,
                              backgroundColor: "#064e2a",
                              color: "#fff",
                              fontWeight: 900,
                              textTransform: "none",
                              borderRadius: "14px",
                              px: 3,
                              boxShadow: "0 12px 26px rgba(6,78,42,0.24)",
                              transition:
                                "transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease",
                              "&:hover": {
                                backgroundColor: "#052e16",
                                transform: "scale(1.06)",
                                boxShadow: "0 16px 34px rgba(6,78,42,0.32)",
                              },
                            }}
                          >
                            Ver partido en vivo
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <Typography
                sx={{
                  color: "#064e2a",
                  fontWeight: 800,
                  textAlign: "center",
                }}
              >
                No hay partidos destacados disponibles en este momento
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/calendario-de-partidos")}
              sx={{
                mt: 3.5,
                backgroundColor: "#064e2a",
                color: "#fff",
                fontWeight: 900,
                textTransform: "none",
                borderRadius: "14px",
                px: 3.4,
                height: 48,
                boxShadow: "0 14px 30px rgba(6,78,42,0.24)",
                transition:
                  "transform 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease",
                "&:hover": {
                  backgroundColor: "#052e16",
                  transform: "scale(1.08)",
                  boxShadow: "0 18px 38px rgba(6,78,42,0.30)",
                },
              }}
            >
              Ver calendario completo
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}